/*
 * Client für die HmiProject-CRUD-Endpoints.
 *
 * Bewusst hier in `hmi/` und nicht in `api/types.ts`: die Wire-Typen dieser
 * Slice gehören zur HMI-Schicht, und `api/types.ts` ist eine geteilte Datei,
 * die gerade von mehreren Seiten angefasst wird.
 *
 * Der Rumpf reist als opaker String (`bodyJson`), genau wie serverseitig.
 * Das Parsen passiert an einer Stelle — {@link parseProjectBody} — damit ein
 * kaputter Rumpf eine lesbare Fehlermeldung ergibt und nicht einen
 * Renderer-Absturz mitten im Screen.
 */

import { api } from '../api/client';
import type { HmiNote, HmiProject as HmiProjectDefinition, HmiScreen, WidgetDefinition } from './widgets';
import { resolveAppearance, type HmiAppearance } from './appearanceModel';
import { compactTheme, parseTheme } from './themeModel';

/** Listen-Eintrag. Ohne Rumpf — die Liste ist ein Wähler. */
export interface HmiProjectSummary {
  id: string;
  plantId: string;
  name: string;
  description: string | null;
  schemaVersion: string;
  revision: number;
  widgetCount: number;
  screenCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Vollständiges Projekt inklusive Rumpf. */
export interface HmiProjectDetail {
  id: string;
  plantId: string;
  name: string;
  description: string | null;
  schemaVersion: string;
  revision: number;
  /** Serialisiertes {@link HmiProjectDefinition}. Opak — über
   *  {@link parseProjectBody} auspacken. */
  bodyJson: string;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  projects: HmiProjectSummary[];
}

export function listHmiProjects(plantId: string, signal?: AbortSignal): Promise<ListResponse> {
  return api.get<ListResponse>(`/api/plants/${plantId}/hmi/projects`, signal);
}

export function getHmiProject(projectId: string, signal?: AbortSignal): Promise<HmiProjectDetail> {
  return api.get<HmiProjectDetail>(`/api/hmi/projects/${projectId}`, signal);
}

export interface CreateHmiProjectInput {
  name: string;
  description?: string;
  schemaVersion?: string;
  /** Fehlt = leeres Projekt. Der AI-Accept-Flow (J.6) füllt das hier. */
  bodyJson?: string;
}

export function createHmiProject(
  plantId: string,
  input: CreateHmiProjectInput,
): Promise<HmiProjectDetail> {
  return api.post<HmiProjectDetail>(`/api/plants/${plantId}/hmi/projects`, input);
}

export interface UpdateHmiProjectInput {
  name?: string;
  description?: string;
  schemaVersion?: string;
  bodyJson?: string;
  /**
   * Der Stand, auf dem diese Aenderung aufbaut.
   *
   * Der Server vergleicht ihn mit dem gespeicherten und antwortet bei
   * Abweichung mit HTTP 409 statt zu ueberschreiben. Weglassen heisst
   * ausdruecklich "ohne Pruefung", und das ist nur fuer Aufrufer gedacht, die
   * kein Bild bearbeiten, etwa ein reines Umbenennen aus der Projektliste.
   * Der Editor schickt ihn immer mit: eine Anordnung von Hand ist nicht
   * rekonstruierbar, wenn sie jemand anders ueberschreibt.
   */
  revision?: number;
}

export function updateHmiProject(
  projectId: string,
  input: UpdateHmiProjectInput,
): Promise<HmiProjectDetail> {
  return api.patch<HmiProjectDetail>(`/api/hmi/projects/${projectId}`, input);
}

export function deleteHmiProject(projectId: string): Promise<void> {
  return api.delete<void>(`/api/hmi/projects/${projectId}`);
}

// ------------------------------------------------------------ KI-Vorschlag

/** Eine Kachel, die es in den Vorschlag geschafft hat. */
export interface SuggestedHmiWidget {
  /** Dieselbe Id wie im {@link HmiSuggestion.bodyJson}. Damit findet eine
   *  Vorschau dieselbe Kachel in beiden Haelften der Antwort wieder. */
  id: string;
  kind: string;
  label: string;
  objectName: string | null;
  /** Feldname der Bindung auf die Leaf-Guid. Der fertige Binding-Key steht
   *  im Rumpf; der Server baut ihn, weil er die Anlage kennt. */
  bindings: Record<string, string>;
  config: Record<string, unknown> | null;
}

/** Eine Kachel, die das Modell vorgeschlagen hat und die nicht gebaut werden
 *  konnte, samt Grund. */
export interface DroppedHmiWidget {
  kind: string;
  label: string | null;
  reason: string;
}

/**
 * Antwort von `POST /api/plants/{plantId}/hmi/ai-suggest`.
 *
 * Zwei Haelften, und beide werden gebraucht. `bodyJson` ist, was gespeichert
 * wird: ein vollstaendiger Projekt-Rumpf, den derselbe Server schon durch
 * seinen Pruefer geschickt hat. Die Felder darueber tragen, was der Rumpf
 * nicht tragen kann, naemlich was das Modell wollte und was auf dem Weg
 * weggefallen ist.
 *
 * Der Rumpf enthaelt genau ein Bild mit einer Anordnung fuer
 * `desktopResponsive`. Mobil und die beiden Panels fehlen mit Absicht: eine
 * geratene Panel-Anordnung sieht aus wie eine gemachte, eine fehlende ist als
 * unfertig erkennbar.
 */
export interface HmiSuggestion {
  /** Eignet sich als Projektname und ist der Name des Bildes im Rumpf. */
  screenName: string;
  screenDescription: string | null;
  rationale: string | null;
  widgets: SuggestedHmiWidget[];
  droppedWidgets: DroppedHmiWidget[];
  /** Je eine Zeile: eine Bindung, deren Leaf-Id es auf dieser Anlage nicht
   *  gibt. */
  droppedBindings: string[];
  /** Je eine Zeile: eine Konfigurationsangabe, die nicht in den Rumpf kam. */
  droppedConfig: string[];
  /** Opak. Geht unveraendert in `createHmiProject` oder `updateHmiProject`. */
  bodyJson: string;
}

/**
 * Einen Bildvorschlag anfordern.
 *
 * Laeuft lange (das Modell liest den ganzen Anlagenbestand), deshalb das
 * `signal`: wer das Modal schliesst, soll die Antwort nicht mehr bekommen.
 *
 * Fehler, die der Aufrufer auseinanderhalten muss:
 * 503 nicht eingerichtet, 502 das Modell antwortete nicht,
 * **500 der Server bekam aus der Antwort des Modells keinen gueltigen Rumpf**.
 * Das letzte ist ausdruecklich nicht "die KI hat nichts gefunden".
 */
export function suggestHmiScreen(
  plantId: string,
  hint: string | null,
  signal?: AbortSignal,
): Promise<HmiSuggestion> {
  // Das Feld heisst serverseitig `prompt`, nicht `hint`. Der Unterschied war
  // am 2026-09-23 im Browser zu sehen: der Server las ein Feld, das nie kam,
  // und antwortete "prompt is too short", obwohl das Modal das Feld als
  // optional beschriftete. Zwei Namen fuer dasselbe Feld sind genau die Sorte
  // Drift, die dieses Repo schon beim Wert-Topic und beim M-Bus getroffen hat.
  return api.post<HmiSuggestion>(
    `/api/plants/${plantId}/hmi/ai-suggest`,
    { prompt: hint ?? '' },
    signal,
  );
}

/** Ergebnis des Auspackens. Fehler wird als Wert zurückgegeben, nicht
 *  geworfen — die Seite soll eine Meldung zeigen können. */
export type ParsedProjectBody =
  | {
      ok: true;
      widgets: WidgetDefinition[];
      screens: HmiScreen[];
      /**
       * Alles, was im Rumpf steht und weder `widgets` noch `screens` ist.
       *
       * Wird durchgereicht und beim Speichern wieder mitgeschrieben. Ohne das
       * verliert jeder Speichervorgang des Editors die Felder, die er nicht
       * kennt, zum Beispiel `schematicAssets`, und zwar still. Ein Editor, der
       * beim Speichern Teile des Dokuments wegwirft, ist schlimmer als einer,
       * der sie nicht bearbeiten kann.
       */
      rest: Record<string, unknown>;
      /** Colour scheme and corners; absent in projects that never set one. */
      appearance?: HmiAppearance;
      /** Neuigkeiten nach Updates (HmiNote); fehlt in Projekten ohne. */
      notes?: HmiNote[];
    }
  | { ok: false; error: string };

/**
 * Packt `bodyJson` aus.
 *
 * Absichtlich defensiv, aber nicht mehr aus dem alten Grund. Hier stand bis
 * zum 2026-09-23, der Server pruefe nur grob und interpretiere die
 * Widget-Arten nicht. Das stimmt seit dem 16.09. nicht mehr:
 * `HmiProjectBodyValidator` deserialisiert jedes Widget einzeln gegen das
 * Definitionsmodell und weist eine unbekannte Art mit einem Befund ab. Ein
 * neues Widget-Kind braucht seitdem einen Server-Deploy, bevor ein Client es
 * speichern darf.
 *
 * Diese Funktion bleibt trotzdem defensiv, und zwar fuer den LESEPFAD: ein
 * Projekt, das ein neuerer Server geschrieben hat, kann Arten enthalten, die
 * dieser Client noch nicht kennt. Dafuer hat `renderWidget` seinen
 * Rueckfall-Zweig. Geprueft wird hier nur, dass die zwei Listen ueberhaupt
 * Listen sind; alles Weitere ist Sache des Servers.
 */
export function parseProjectBody(bodyJson: string): ParsedProjectBody {
  let raw: unknown;
  try {
    raw = JSON.parse(bodyJson);
  } catch (err) {
    return { ok: false, error: `Projekt-Rumpf ist kein gültiges JSON: ${(err as Error).message}` };
  }
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, error: 'Projekt-Rumpf ist kein JSON-Objekt.' };
  }
  const body = raw as Partial<HmiProjectDefinition>;
  const widgets = body.widgets;
  const screens = body.screens;
  if (widgets !== undefined && !Array.isArray(widgets)) {
    return { ok: false, error: "'widgets' im Projekt-Rumpf ist kein Array." };
  }
  if (screens !== undefined && !Array.isArray(screens)) {
    return { ok: false, error: "'screens' im Projekt-Rumpf ist kein Array." };
  }
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (key === 'widgets' || key === 'screens' || key === 'appearance' || key === 'notes') continue;
    rest[key] = value;
  }
  const rawAppearance = (raw as Record<string, unknown>).appearance;
  // The theme rides along inside `appearance`. resolveAppearance only knows
  // palette and corners; without this line every save from the editor
  // dropped a theme that the author (or the gateway contract) had set.
  const rawTheme = rawAppearance !== null && typeof rawAppearance === 'object'
    ? (rawAppearance as Record<string, unknown>).theme
    : undefined;
  const theme = rawTheme === undefined ? null : parseTheme(rawTheme);
  const appearance = rawAppearance === undefined
    ? undefined
    : { ...resolveAppearance(rawAppearance), ...(theme ? { theme: compactTheme(theme) } : {}) };

  const rawNotes = (raw as Record<string, unknown>).notes;
  const notes = Array.isArray(rawNotes)
    ? rawNotes.filter((n): n is HmiNote => !!n && typeof n === 'object'
        && typeof (n as HmiNote).id === 'string' && typeof (n as HmiNote).title === 'string'
        && typeof (n as HmiNote).text === 'string')
    : undefined;
  return { ok: true, widgets: widgets ?? [], screens: screens ?? [], rest, appearance, notes };
}

/** Packt Widgets und Screens wieder zu einem Rumpf zusammen. Gegenstück zu
 *  {@link parseProjectBody}; von einem künftigen Layout-Editor und vom
 *  AI-Accept-Flow gebraucht. */
export function serialiseProjectBody(
  widgets: readonly WidgetDefinition[],
  screens: readonly HmiScreen[],
  rest?: Record<string, unknown>,
  appearance?: HmiAppearance,
  notes?: readonly HmiNote[],
): string {
  // `rest` zuerst, damit `widgets` und `screens` es in jedem Fall
  // ueberschreiben und nicht umgekehrt. Ein alter Rumpf, der dieselben
  // Schluessel noch einmal traegt, darf den aktuellen Stand nicht ersetzen.
  // Appearance only when set, so a project that never chose one keeps the
  // exact body it had.
  return JSON.stringify({
    ...(rest ?? {}), ...(appearance ? { appearance } : {}), ...(notes && notes.length > 0 ? { notes } : {}), widgets, screens,
  });
}
